// src/pages/IngresoFactura.js
import React, { useState, useEffect } from "react";
import { 
  Button, 
  Container, 
  Typography, 
  Alert, 
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Box,
  Grid
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSftfApi } from "../api/sftfClient";
import { fileToBase64 } from "../utils/fileToBase64";

export default function IngresoFactura() {
  const { auth } = useAuth();
  const { postActualiza, consultarTiposFactura, consultarEstatusFactura } = useSftfApi(auth);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [tiposFactura, setTiposFactura] = useState([]);
  const [estatusFactura, setEstatusFactura] = useState([]);
  const [cargandoCatalogos, setCargandoCatalogos] = useState(true);
  const location = useLocation();
  
  // Obtener valores prepopulados si existen
  const prepopulatedValues = location.state?.prepopulatedValues || {};

  // Valores iniciales del formulario
  const initialValues = {
    xmlFile: null,
    pdfFile: null,
    claveTipoFactura: prepopulatedValues.claveTipoFactura || "",
    SerieBitacora: prepopulatedValues.SerieBitacora || "",
    FolioBitacora: prepopulatedValues.FolioBitacora || "",
    SerieRemision: prepopulatedValues.SerieRemision || "",
    FolioRemision: prepopulatedValues.FolioRemision || "",
    SerieFactura: prepopulatedValues.SerieFactura || "",
    FolioFactura: prepopulatedValues.FolioFactura || "",
    RemitenteRFC: prepopulatedValues.RemitenteRFC || "",
    estatus: prepopulatedValues.estatus || prepopulatedValues.Estatus || "",
    origen: prepopulatedValues.origen || "",
    destino: prepopulatedValues.destino || "",
    km: prepopulatedValues.km || "",
    conductor: prepopulatedValues.conductor || "",
    peso: prepopulatedValues.peso || "",
    mercanciaDescripcion: prepopulatedValues.mercanciaDescripcion || ""
  };

  // Cargar catálogos al montar el componente
  useEffect(() => {
    const cargarCatalogos = async () => {
      try {
        setCargandoCatalogos(true);

        // Ambos catálogos se piden en paralelo: antes se esperaban en
        // secuencia y, con un hub lento, cada uno podía tardar hasta ~93 s
        // (3 intentos, corte a 45 s), dejando el formulario bloqueado casi
        // 3 minutos en el peor caso.
        const [tiposData, estatusData] = await Promise.all([
          consultarTiposFactura(),
          consultarEstatusFactura(),
        ]);

        if (tiposData && Array.isArray(tiposData)) {
          setTiposFactura(tiposData);
        }
        if (estatusData && Array.isArray(estatusData)) {
          setEstatusFactura(estatusData);
        }
      } catch (err) {
        console.error("Error al cargar catálogos:", err);
        setError("Error al cargar los catálogos de tipos de factura y estatus");
      } finally {
        setCargandoCatalogos(false);
      }
    };

    cargarCatalogos();
  }, [consultarTiposFactura, consultarEstatusFactura]);

  // Mostrar mensaje si se recibieron valores prepopulados
  const hasPrepopulatedValues = Object.values(prepopulatedValues).some(value => value !== "");

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Envío de Factura Electrónica
      </Typography>
      
      {hasPrepopulatedValues && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Los campos han sido prepopulados con los datos de la bitácora seleccionada.
        </Alert>
      )}

      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={Yup.object({
          claveTipoFactura: Yup.string().required("El tipo de factura es requerido"),
          SerieBitacora: Yup.string().required("Requerido"),
          FolioBitacora: Yup.string().required("Requerido"),
          SerieRemision: Yup.string().required("Requerido"),
          FolioRemision: Yup.string().required("Requerido"),
          SerieFactura: Yup.string(),
          FolioFactura: Yup.string(),
          RemitenteRFC: Yup.string().required("Requerido"),
          estatus: Yup.string().required("El estatus es requerido"),
          xmlFile: Yup.mixed().required("XML requerido"),
          pdfFile: Yup.mixed().required("PDF requerido")
        })}
        onSubmit={async (values) => {
          try {
            const xmlBase64 = await fileToBase64(values.xmlFile);
            const pdfBase64 = await fileToBase64(values.pdfFile);

            const factura = {
              ClaveTipoFactura: parseInt(values.claveTipoFactura),
              SerieBitacora: values.SerieBitacora,
              FolioBitacora: parseInt(values.FolioBitacora),
              SerieRemision: values.SerieRemision,
              FolioRemision: parseInt(values.FolioRemision),
              SerieFactura: values.SerieFactura,
              FolioFactura: values?.FolioFactura ? parseInt(values.FolioFactura): "",
              RemitenteRFC: values.RemitenteRFC,
              Estatus: parseInt(values.estatus),
              AttachPathXML: xmlBase64,
              AttachPathPDF: pdfBase64
            };

            const res = await postActualiza([factura]);
            setResult(res);
            setError(null);
          } catch (err) {
            setError(err.message || "Error desconocido");
            setResult(null);
          }
        }}
      >
        {({ setFieldValue, values, errors, touched }) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth margin="normal" size="large" sx={{ minHeight: '56px' }}>
                  <InputLabel sx={{ fontSize: '1.1rem' }}>Tipo de Factura</InputLabel>
                  <Field
                    as={Select}
                    name="claveTipoFactura"
                    label="Tipo de Factura"
                    disabled={cargandoCatalogos}
                    displayEmpty
                    error={touched.claveTipoFactura && !!errors.claveTipoFactura}
                    renderValue={(selected) => {
                      if (!selected || selected === '') {
                        return <em>Seleccione un tipo de factura</em>;
                      }
                      const tipo = tiposFactura.find(t => t.ClaveTipoFactura.toString() === selected);
                      return tipo ? `${tipo.Descripcion} (${tipo.TipoFactura})` : selected;
                    }}
                    sx={{ 
                      fontSize: '1.1rem',
                      minHeight: '56px',
                      '& .MuiSelect-select': {
                        padding: '16px 14px',
                        fontSize: '1.1rem',
                        minHeight: '24px',
                        display: 'flex',
                        alignItems: 'center'
                      }
                    }}
                  >
                    {cargandoCatalogos ? (
                      <MenuItem disabled>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={16} />
                          <span>Cargando tipos...</span>
                        </Box>
                      </MenuItem>
                    ) : (
                      tiposFactura.map((tipo) => (
                        <MenuItem 
                          key={tipo.ClaveTipoFactura} 
                          value={tipo.ClaveTipoFactura.toString()}
                          sx={{ fontSize: '1.1rem', padding: '12px 16px' }}
                        >
                          {tipo.Descripcion} ({tipo.TipoFactura})
                        </MenuItem>
                      ))
                    )}
                  </Field>
                </FormControl>
                {touched.claveTipoFactura && errors.claveTipoFactura && (
                  <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                    {errors.claveTipoFactura}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth margin="normal" size="large" sx={{ minHeight: '56px' }}>
                  <InputLabel sx={{ fontSize: '1.1rem' }}>Estatus</InputLabel>
                  <Field
                    as={Select}
                    name="estatus"
                    label="Estatus"
                    disabled={cargandoCatalogos}
                    displayEmpty
                    error={touched.estatus && !!errors.estatus}
                    renderValue={(selected) => {
                      if (!selected || selected === '') {
                        return <em>Seleccione un estatus</em>;
                      }
                      const estatus = estatusFactura.find(e => e.ClaveEstatusFactura.toString() === selected);
                      return estatus ? estatus.EstatusFactura : selected;
                    }}
                    sx={{ 
                      fontSize: '1.1rem',
                      minHeight: '56px',
                      '& .MuiSelect-select': {
                        padding: '16px 14px',
                        fontSize: '1.1rem',
                        minHeight: '24px',
                        display: 'flex',
                        alignItems: 'center'
                      }
                    }}
                  >
                    {cargandoCatalogos ? (
                      <MenuItem disabled>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={16} />
                          <span>Cargando estatus...</span>
                        </Box>
                      </MenuItem>
                    ) : (
                      estatusFactura.map((estatus) => (
                        <MenuItem 
                          key={estatus.ClaveEstatusFactura} 
                          value={estatus.ClaveEstatusFactura.toString()}
                          sx={{ fontSize: '1.1rem', padding: '12px 16px' }}
                        >
                          {estatus.EstatusFactura}
                        </MenuItem>
                      ))
                    )}
                  </Field>
                </FormControl>
                {touched.estatus && errors.estatus && (
                  <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                    {errors.estatus}
                  </Typography>
                )}
              </Grid>
            </Grid>
            
            <Field name="xmlFile">
              {({ field }) => (
                <TextField
                  type="file"
                  fullWidth
                  margin="normal"
                  inputProps={{ accept: ".xml" }}
                  label="Archivo XML"
                  onChange={(e) => setFieldValue("xmlFile", e.currentTarget.files[0])}
                />
              )}
            </Field>
            <Field name="pdfFile">
              {({ field }) => (
                <TextField
                  type="file"
                  fullWidth
                  margin="normal"
                  inputProps={{ accept: ".pdf" }}
                  label="Archivo PDF"
                  onChange={(e) => setFieldValue("pdfFile", e.currentTarget.files[0])}
                />
              )}
            </Field>
            <TextField 
              label="Serie Bitácora" 
              name="SerieBitacora" 
              fullWidth 
              margin="normal" 
              value={values.SerieBitacora}
              onChange={e => setFieldValue("SerieBitacora", e.target.value)} 
            />
            <TextField 
              label="Folio Bitácora" 
              name="FolioBitacora" 
              fullWidth 
              margin="normal" 
              value={values.FolioBitacora}
              onChange={e => setFieldValue("FolioBitacora", e.target.value)} 
            />
            <TextField 
              label="Serie Remisión" 
              name="SerieRemision" 
              fullWidth 
              margin="normal" 
              value={values.SerieRemision}
              onChange={e => setFieldValue("SerieRemision", e.target.value)} 
            />
            <TextField 
              label="Folio Remisión" 
              name="FolioRemision" 
              fullWidth 
              margin="normal" 
              value={values.FolioRemision}
              onChange={e => setFieldValue("FolioRemision", e.target.value)} 
            />
            <TextField 
              label="Serie Factura" 
              name="SerieFactura" 
              fullWidth 
              margin="normal" 
              value={values.SerieFactura}
              onChange={e => setFieldValue("SerieFactura", e.target.value)} 
            />
            <TextField 
              label="Folio Factura" 
              name="FolioFactura" 
              fullWidth 
              margin="normal" 
              value={values.FolioFactura}
              onChange={e => setFieldValue("FolioFactura", e.target.value)} 
            />
            <TextField 
              label="RFC Remitente" 
              name="RemitenteRFC" 
              fullWidth 
              margin="normal" 
              value={values.RemitenteRFC}
              onChange={e => setFieldValue("RemitenteRFC", e.target.value)} 
            />
            
            {/* Campos no modificables */}
            <Typography variant="h6" sx={{ mt: 3, mb: 2, color: 'text.secondary' }}>
              Información de la Bitácora (Solo lectura)
            </Typography>
            
            <TextField 
              label="Origen" 
              name="origen" 
              fullWidth 
              margin="normal" 
              value={values.origen}
              InputProps={{ readOnly: true }}
              sx={{ '& .MuiInputBase-input': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
            />
            <TextField 
              label="Destino" 
              name="destino" 
              fullWidth 
              margin="normal" 
              value={values.destino}
              InputProps={{ readOnly: true }}
              sx={{ '& .MuiInputBase-input': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
            />
            <TextField 
              label="Kilómetros" 
              name="km" 
              fullWidth 
              margin="normal" 
              value={values.km}
              InputProps={{ readOnly: true }}
              sx={{ '& .MuiInputBase-input': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
            />
            <TextField 
              label="Conductor" 
              name="conductor" 
              fullWidth 
              margin="normal" 
              value={values.conductor}
              InputProps={{ readOnly: true }}
              sx={{ '& .MuiInputBase-input': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
            />
            <TextField 
              label="Peso" 
              name="peso" 
              fullWidth 
              margin="normal" 
              value={values.peso}
              InputProps={{ readOnly: true }}
              sx={{ '& .MuiInputBase-input': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
            />
            <TextField 
              label="Descripción de Mercancía" 
              name="mercanciaDescripcion" 
              fullWidth 
              margin="normal" 
              multiline
              rows={3}
              value={values.mercanciaDescripcion}
              InputProps={{ readOnly: true }}
              sx={{ '& .MuiInputBase-input': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
            />
            
            <Button type="submit" variant="contained" fullWidth sx={{ mt: 2, mb: 3 }}>
              Enviar Factura
            </Button>
          </Form>
        )}
      </Formik>
      {result && <pre>{JSON.stringify(result, null, 2)}</pre>}
      {error && <Alert severity="error">{error}</Alert>}
    </Container>
  );
}
